import api from './api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';

export const generationsService = {
  async generateText(data) {
    // API expects modelSlug, not model
    // Only include optional fields if they have values (API validation rejects null)
    const payload = {
      modelSlug: data.model || data.modelSlug,
      prompt: data.prompt,
    };
    if (data.maxTokens) payload.maxTokens = data.maxTokens;
    if (data.temperature !== undefined) payload.temperature = data.temperature;
    if (data.conversationId) payload.conversationId = data.conversationId;

    const response = await api.post('/generate/text', payload);
    return response.data;
  },

  // Streaming text generation using SSE
  async generateTextStream(data, onChunk, onComplete, onError) {
    const token = localStorage.getItem('accessToken');
    // API expects modelSlug, not model
    // Only include optional fields if they have values
    const payload = {
      modelSlug: data.model || data.modelSlug,
      prompt: data.prompt,
    };
    if (data.maxTokens) payload.maxTokens = data.maxTokens;
    if (data.temperature !== undefined) payload.temperature = data.temperature;
    if (data.conversationId) payload.conversationId = data.conversationId;
    if (data.messages) payload.messages = data.messages;

    try {
      const response = await fetch(`${API_BASE_URL}/generate/text/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Stream request failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              onComplete?.();
              return;
            }
            try {
              const parsed = JSON.parse(data);
              onChunk?.(parsed);
            } catch (e) {
              // Not valid JSON, might be partial content
              onChunk?.({ content: data });
            }
          }
        }
      }

      onComplete?.();
    } catch (error) {
      onError?.(error);
    }
  },

  async generateImage(data) {
    const payload = {
      modelSlug: data.model || data.modelSlug,
      prompt: data.prompt,
      size: data.size,
      quality: data.quality,
      n: data.n,
    };
    const response = await api.post('/generate/image', payload);
    return response.data;
  },

  async generateVideo(data) {
    const payload = {
      modelSlug: data.model || data.modelSlug,
      prompt: data.prompt,
      duration: data.duration,
      aspectRatio: data.aspectRatio,
    };
    const response = await api.post('/generate/video', payload);
    return response.data;
  },

  async generateAudio(data) {
    const payload = {
      modelSlug: data.model || data.modelSlug,
      text: data.text,
      voiceId: data.voiceId,
    };
    const response = await api.post('/generate/audio', payload);
    return response.data;
  },

  async getGenerations(params = {}) {
    const response = await api.get('/generations', { params });
    return response.data;
  },

  async getGeneration(id) {
    const response = await api.get(`/generations/${id}`);
    return response.data;
  },

  async getRecentGenerations() {
    const response = await api.get('/generations/recent');
    return response.data;
  },

  async getStats() {
    const response = await api.get('/generations/stats');
    return response.data;
  },
};

export default generationsService;
